import { Lightning } from "@phosphor-icons/react";

/**
 * Personalized Pre-501(c)(3) Founder Playbook for Ark of Refuge (Jeannine).
 * Print-optimized: light background, dark text, clean page breaks.
 *
 * Route: /playbook/ark-of-refuge
 *
 * Greg's flow:
 *   1. Open the URL on a laptop
 *   2. Cmd/Ctrl + P → Save as PDF → email to Jeannine
 *   3. Or share the live URL directly (works on mobile)
 */

const Section = ({ eyebrow, title, children, breakBefore = false }) => (
  <section
    className={`mb-12 ${breakBefore ? "print:break-before-page" : ""}`}
    data-testid={`playbook-section-${(eyebrow || title || "").toLowerCase().replace(/\s+/g, "-")}`}
  >
    {eyebrow && (
      <div className="text-emerald-700 font-bold tracking-[0.18em] text-xs uppercase mb-2">
        {eyebrow}
      </div>
    )}
    <h2 className="text-3xl font-black text-zinc-900 mb-6 leading-tight">{title}</h2>
    <div className="text-zinc-800 leading-relaxed space-y-4 text-base">{children}</div>
  </section>
);

const Callout = ({ children, tone = "emerald" }) => {
  const styles =
    tone === "emerald"
      ? "bg-emerald-50 border-emerald-500 text-emerald-900"
      : tone === "amber"
      ? "bg-amber-50 border-amber-500 text-amber-900"
      : "bg-zinc-50 border-zinc-400 text-zinc-800";
  return (
    <div className={`border-l-4 ${styles} p-4 rounded-r my-4`}>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const PlaybookArkOfRefuge = () => {
  return (
    <div className="min-h-screen bg-white text-zinc-900 print:bg-white" data-testid="playbook-page">
      {/* ─── HEADER BAND ─── */}
      <header className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950 text-white px-12 py-10 print:px-10 print:py-8">
        <div className="max-w-[800px] mx-auto">
          <div className="flex items-center gap-2.5 mb-8">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-300" />
            <span className="font-bold tracking-tight text-xl">FlashGrants</span>
          </div>
          <div className="text-emerald-300 font-bold tracking-[0.18em] text-xs uppercase mb-3">
            Pre-501(c)(3) Founder Playbook · Personalized
          </div>
          <h1 className="text-5xl font-black tracking-tight leading-tight mb-3">
            For Jeannine &amp; Ark of Refuge
          </h1>
          <p className="text-emerald-100 text-lg leading-relaxed max-w-[640px]">
            Your 90-day path from “still in planning stage” to funded, sponsored, and serving
            more mothers and babies than you thought possible — with the work you’ve
            already been doing.
          </p>
          <p className="text-emerald-300/80 text-sm mt-4 font-mono">
            Prepared by Greg James · FlashGrants · June 16, 2026
          </p>
        </div>
      </header>

      {/* ─── BODY ─── */}
      <main className="max-w-[800px] mx-auto px-12 py-12 print:px-10 print:py-8">

        {/* OPENING NOTE */}
        <Section eyebrow="A Word Before You Begin" title="The vision was never the problem.">
          <p>
            Three years ago, God gave you a vision you didn’t think was possible. The
            community baby shower in October 2025 — when the pampers came in and your friend
            stood up for you — was the moment it became real. Since then, you’ve helped
            mothers find their footing. You’ve passed the work to your two adult children
            who still serve. You’ve built a daycare-keeper’s understanding of what it takes
            to run something for kids.
          </p>
          <p>
            What you were missing wasn’t faith, work ethic, or vision. It was a map.
          </p>
          <p>
            This document is your map. Read it once. Print it. Show it to your board, your
            family, your church. The path is real. The grants exist. And you are exactly
            who they were written for.
          </p>
        </Section>

        {/* SNAPSHOT */}
        <Section eyebrow="Your Foundation — A Quick Snapshot" title="Where Ark of Refuge stands today.">
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-5">
            <table className="w-full text-sm">
              <tbody className="space-y-1">
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top w-44">Founder</td><td className="text-zinc-900 py-1.5">Jeannine</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Mission</td><td className="text-zinc-900 py-1.5">Wraparound support for young, single, and teen mothers — needs assessment, planning, supplies, and (in time) an in-house therapist</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Counties served</td><td className="text-zinc-900 py-1.5">Georgetown, Williamsburg <span className="text-zinc-500">(expanding into Horry)</span></td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Current operating budget</td><td className="text-zinc-900 py-1.5">~$10,000 revenue / ~$5,000 expenses</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Programs</td><td className="text-zinc-900 py-1.5">Baby essentials distribution · Workshops · Referral assistance · Motivational speaking</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">Faith-driven?</td><td className="text-zinc-900 py-1.5">Yes — this unlocks specific grant streams (see Section 3)</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">3-year vision</td><td className="text-zinc-900 py-1.5">A distribution center where homeless mothers and babies can stay and be served</td></tr>
                <tr><td className="font-semibold text-zinc-700 pr-4 py-1.5 align-top">501(c)(3) status</td><td className="text-zinc-900 py-1.5">In planning — pursuing hybrid path (see Section 1)</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* ─── SECTION 1: THE HYBRID PATH ─── */}
        <Section eyebrow="Section 1 of 5" title="Your path: Fiscal sponsor now, your own 501(c)(3) in 90 days." breakBefore>
          <p>
            You’re taking the smartest path most pre-501(c)(3) founders never even know
            exists — the <strong>Hybrid Path</strong>. You’ll be fiscal-sponsored AND
            filing your own 1023-EZ application at the same time. This gives you immediate
            grant access while you build long-term independence.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6 print:grid-cols-2">
            <div className="border-2 border-emerald-500 rounded-lg p-5 bg-emerald-50/50">
              <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Track 1 · This week</div>
              <h3 className="font-bold text-zinc-900 text-lg mb-2">Coastal Community Foundation Fiscal Sponsorship</h3>
              <p className="text-sm text-zinc-700 leading-snug mb-3">
                Apply for fiscal sponsorship through CCF’s program. They host you under
                their 501(c)(3) umbrella while you operate independently.
              </p>
              <ul className="text-sm text-zinc-700 space-y-1.5 list-none">
                <li>· Timeline: 4–8 weeks to approval</li>
                <li>· Fee: variable (often %-of-grants)</li>
                <li>· Unlocks: Care Fund (up to $25K)</li>
                <li>· Bonus: CCF brand credibility in SC</li>
              </ul>
            </div>
            <div className="border-2 border-zinc-300 rounded-lg p-5 bg-zinc-50/50">
              <div className="text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1">Track 2 · This month</div>
              <h3 className="font-bold text-zinc-900 text-lg mb-2">File Your Own IRS Form 1023-EZ</h3>
              <p className="text-sm text-zinc-700 leading-snug mb-3">
                File the streamlined 501(c)(3) application directly with the IRS.
                3-page form. 90-day average review time.
              </p>
              <ul className="text-sm text-zinc-700 space-y-1.5 list-none">
                <li>· Timeline: ~90 days to determination letter</li>
                <li>· Cost: $275 one-time IRS fee</li>
                <li>· Unlocks: full independence + every grant</li>
                <li>· Bonus: no sponsor fees forever after</li>
              </ul>
            </div>
          </div>

          <Callout tone="emerald">
            <strong>Why this is the smartest path:</strong> 4-6 months from now, when your
            determination letter arrives, you “graduate” out of CCF sponsorship and operate
            standalone. By then you’ll have one or two grants on your record and a board
            ready to take over. Most pre-501(c)(3) founders pick one path. You’re doing both.
            That’s a year of progress in 90 days.
          </Callout>

          <h3 className="font-bold text-zinc-900 text-xl mt-8 mb-3">This week’s two specific actions:</h3>
          <ol className="space-y-3 list-decimal list-outside ml-6">
            <li>
              <strong>Visit coastalcommunityfoundation.org</strong> and find the “Fiscal
              Sponsorship Program” page. Submit the initial inquiry form. Expect a response
              within 7-10 business days requesting more information.
            </li>
            <li>
              <strong>Visit irs.gov</strong> and search “Form 1023-EZ Eligibility
              Worksheet.” Complete the 26-question eligibility test. If you pass (most
              small organizations do), you can file the EZ form for $275 instead of the
              full Form 1023 which costs $600 and is far more complex.
            </li>
          </ol>
        </Section>

        {/* ─── SECTION 2: GRANTS TO CHASE ─── */}
        <Section eyebrow="Section 2 of 5" title="The first three grants to chase." breakBefore>
          <p>
            Once you’re fiscal-sponsored through CCF (estimated July 2026), these three
            grants become available to you immediately. Listed in the order you should
            apply for them.
          </p>

          <div className="space-y-5 my-6">
            {/* Grant 1 */}
            <div className="border border-zinc-200 rounded-lg p-5 bg-white">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div>
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">First grant · Best fit</div>
                  <h3 className="text-lg font-bold text-zinc-900">Coastal Community Foundation — The Care Fund</h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-emerald-600">Up to $25,000</div>
                  <div className="text-xs text-zinc-500 font-mono">Rolling · Open now</div>
                </div>
              </div>
              <p className="text-sm text-zinc-700 leading-snug">
                CCF’s own grant. Funds organizations addressing community needs in their
                9-county service area. Explicitly accepts fiscal-sponsored projects.
                Sponsorship and Care Fund eligibility move together — when CCF sponsors
                you, you’re already inside their funnel.
              </p>
              <p className="text-sm text-zinc-700 leading-snug mt-2">
                <strong>Recommended ask:</strong> $9,999 (under their probable discretion
                threshold, well within their grant range). Targeted at the wraparound
                support program for young mothers.
              </p>
            </div>

            {/* Grant 2 */}
            <div className="border border-zinc-200 rounded-lg p-5 bg-white">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div>
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Second grant · Georgetown County is your home turf</div>
                  <h3 className="text-lg font-bold text-zinc-900">Frances P. Bunnelle Foundation — Mini Grants</h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-emerald-600">Up to $5,000</div>
                  <div className="text-xs text-zinc-500 font-mono">Rolling deadline</div>
                </div>
              </div>
              <p className="text-sm text-zinc-700 leading-snug">
                Bunnelle is Georgetown County’s biggest youth and family funder. They
                fund organizations that serve Georgetown County kids and the families
                supporting them. Your work fits.
              </p>
              <p className="text-sm text-zinc-700 leading-snug mt-2">
                <strong>The Kevin McCray move:</strong> Their program officer (Kevin
                McCray) actively wants a 15-minute phone conversation before any
                application. Once you’re sponsored, call him. Greg can make a warm
                introduction.
              </p>
            </div>

            {/* Grant 3 */}
            <div className="border border-zinc-200 rounded-lg p-5 bg-white">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div>
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Third grant · Faith-aligned mission match</div>
                  <h3 className="text-lg font-bold text-zinc-900">SC Christian Foundation — Circle 555</h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-emerald-600">Up to $10,000</div>
                  <div className="text-xs text-zinc-500 font-mono">Deadline: Nov 30, 2026</div>
                </div>
              </div>
              <p className="text-sm text-zinc-700 leading-snug">
                Circle 555 funds Christian-mission organizations serving women, children,
                and families. Your faith-driven mission is precisely what they fund.
                Apply by mid-November once you’re sponsored.
              </p>
            </div>
          </div>

          <Callout tone="amber">
            <strong>Bunnelle Spring Common Good Cycle</strong> ($15,000, April 2027
            deadline) is on the radar for later in your journey — once you’ve landed Care
            Fund and one Bunnelle Mini, you’ll be a credible candidate for their bigger
            cycle.
          </Callout>
        </Section>

        {/* ─── SECTION 3: WILLIAMSBURG PPC ─── */}
        <Section eyebrow="Section 3 of 5" title="The Williamsburg County advantage no one tells founders about." breakBefore>
          <p>
            Williamsburg County is one of only <strong>12 federally-designated Persistent
            Poverty Counties</strong> in South Carolina — meaning counties where the
            poverty rate has remained above 20% for over thirty consecutive years (U.S.
            Census Bureau, May 2023 publication).
          </p>
          <p>
            Federal grant programs — USDA, HRSA, EDA, the CDFI Fund — are required by law
            to direct at least 10% of certain funding pools to PPCs. Some programs (like
            USDA’s Rural Business Development Grant) automatically award 10 priority
            points on the scoring rubric when an applicant works in one.
          </p>
          <p>
            <strong>You serve Williamsburg County.</strong> You qualify for this scoring
            boost. Most SC nonprofits never claim it. You will.
          </p>

          <Callout tone="emerald">
            <p className="font-bold text-emerald-900 mb-2">Paste this paragraph into every grant application you write:</p>
            <p className="text-emerald-950 italic leading-relaxed">
              “Ark of Refuge serves young, single, and teen mothers and their children
              across Georgetown and Williamsburg Counties — including Williamsburg County,
              one of twelve federally-designated Persistent Poverty Counties in South
              Carolina (U.S. Census Bureau, &lsquo;Persistent Poverty in Counties and Census
              Tracts,&rsquo; May 2023). Our work directly serves the population the federal
              government has identified as the most under-resourced in our state across
              multiple generations.”
            </p>
          </Callout>

          <p>
            Reviewers literally search the document for the phrase <em>“Persistent
            Poverty Counties as designated by the U.S. Census Bureau.”</em> If it’s there,
            they have to score it. If it’s missing, they don’t know to give you the
            points. Use it.
          </p>
        </Section>

        {/* ─── SECTION 4: 90-DAY PLAN ─── */}
        <Section eyebrow="Section 4 of 5" title="Your 90-day action plan." breakBefore>
          <p>Week by week. One thing at a time. No skipping ahead.</p>

          <div className="space-y-4 my-6">
            {[
              {
                week: "Week 1",
                date: "June 16–22",
                focus: "Foundation in place",
                actions: [
                  "Take the Mission Statement Diagnostic at flash-grants.com/formation/mission-diagnostic (5 min — free)",
                  "Submit Coastal Community Foundation Fiscal Sponsorship inquiry form",
                  "Open a free Google Drive folder titled 'Ark of Refuge — Foundation Documents'",
                ],
              },
              {
                week: "Week 2",
                date: "June 23–29",
                focus: "Paperwork & people",
                actions: [
                  "Take the IRS Form 1023-EZ Eligibility Worksheet (confirm you qualify)",
                  "Recruit/confirm 3-5 board members. They can be friends or community leaders who believe in the work.",
                  "Draft a one-paragraph mission statement using your Diagnostic score feedback",
                ],
              },
              {
                week: "Week 3",
                date: "June 30 – July 6",
                focus: "Story collection",
                actions: [
                  "Write down the story of three specific mothers Ark of Refuge has helped (no names if needed)",
                  "Take photos of program activities, donated supplies, your community baby shower memorabilia",
                  "Begin drafting Form 1023-EZ application in your Google Drive folder",
                ],
              },
              {
                week: "Week 4-6",
                date: "July 7-27",
                focus: "Submit & wait",
                actions: [
                  "Submit IRS Form 1023-EZ ($275 fee — pay.gov)",
                  "Respond to any CCF follow-up requests for sponsorship application",
                  "Schedule your check-in call with Greg (Greg suggested July 1 — confirm or adjust)",
                ],
              },
              {
                week: "Week 7-9",
                date: "Late July – mid August",
                focus: "Sponsorship approved · Apply",
                actions: [
                  "CCF approves your fiscal sponsorship (expected window)",
                  "Submit your first Care Fund application — recommended ask: $9,999",
                  "Get warm intro to Kevin McCray at Bunnelle through Greg",
                ],
              },
              {
                week: "Week 10-13",
                date: "September",
                focus: "Build the second grant",
                actions: [
                  "Submit Bunnelle Mini Grants application ($5,000)",
                  "Begin drafting SC Christian Foundation Circle 555 application",
                  "Track first donations under fiscal sponsor arrangement",
                ],
              },
            ].map((w) => (
              <div key={w.week} className="border-l-4 border-emerald-500 pl-5 py-2">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
                  <h4 className="font-bold text-zinc-900 text-lg">{w.week} <span className="text-zinc-400 font-normal text-sm font-mono">· {w.date}</span></h4>
                  <span className="text-xs uppercase tracking-wider text-emerald-700 font-bold">{w.focus}</span>
                </div>
                <ul className="text-sm text-zinc-700 space-y-1 list-disc list-outside ml-5">
                  {w.actions.map((a, i) => (<li key={i}>{a}</li>))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* ─── SECTION 5: RESOURCES ─── */}
        <Section eyebrow="Section 5 of 5" title="The resources page." breakBefore>
          <p>Save this. Print it. Pin it on your refrigerator.</p>

          <div className="my-6">
            <h3 className="font-bold text-zinc-900 text-lg mb-3">Fiscal Sponsorship</h3>
            <ul className="space-y-2 text-sm text-zinc-700 list-disc list-outside ml-5">
              <li><strong>Coastal Community Foundation:</strong> coastalcommunityfoundation.org → “Fiscal Sponsorship Program”</li>
              <li><strong>Players Philanthropy Fund</strong> (backup, faster): playersphilanthropy.com (10-day approval)</li>
            </ul>

            <h3 className="font-bold text-zinc-900 text-lg mb-3 mt-6">Your Own 501(c)(3)</h3>
            <ul className="space-y-2 text-sm text-zinc-700 list-disc list-outside ml-5">
              <li><strong>IRS Form 1023-EZ:</strong> irs.gov → search “Form 1023-EZ”</li>
              <li><strong>SC Secretary of State Charity Registration:</strong> sos.sc.gov/charity</li>
              <li><strong>Pay.gov (for the $275 fee):</strong> pay.gov</li>
            </ul>

            <h3 className="font-bold text-zinc-900 text-lg mb-3 mt-6">Grant Research</h3>
            <ul className="space-y-2 text-sm text-zinc-700 list-disc list-outside ml-5">
              <li><strong>FlashGrants Sunday Drop:</strong> flash-grants.com — five SC grants every Sunday in your inbox</li>
              <li><strong>ProPublica Nonprofit Explorer</strong> (free 990 lookups for any foundation): projects.propublica.org/nonprofits</li>
              <li><strong>Frances P. Bunnelle Foundation:</strong> bunnelle.org — program officer Kevin McCray</li>
              <li><strong>SC Christian Foundation:</strong> sccf.org — Circle 555 program</li>
            </ul>

            <h3 className="font-bold text-zinc-900 text-lg mb-3 mt-6">Bonus: Federal Persistent Poverty County Reference</h3>
            <ul className="space-y-2 text-sm text-zinc-700 list-disc list-outside ml-5">
              <li><strong>U.S. Census Bureau publication</strong> (May 2023): census.gov → search “Persistent Poverty in Counties and Census Tracts”</li>
              <li><strong>SC&apos;s 12 PPCs:</strong> Allendale · Bamberg · Barnwell · Dillon · Hampton · Lee · Marion · Marlboro · Orangeburg · Sumter · Union · <strong>Williamsburg ✓</strong> (your county)</li>
            </ul>
          </div>

          <Callout tone="emerald">
            <strong>Your next call with Greg:</strong> tentatively July 1 (confirm/adjust as
            needed). Bring: your CCF inquiry confirmation, your Form 1023-EZ progress, and
            the names of any potential board members. Greg will bring: a draft Care Fund
            application outline and a warm intro to Bunnelle.
          </Callout>
        </Section>

        {/* CLOSING NOTE */}
        <Section eyebrow="One Last Thing" title="The work matters. The map is real. Walk it.">
          <p>
            Jeannine — you’ve already done the hardest part. You said yes to the vision
            when you didn’t know how. You showed up at the community baby shower in
            October and let it become real. You raised two children who still help
            people. You ran a daycare and learned what it takes.
          </p>
          <p>
            The grants aren’t gatekept by anyone but the people who never knew the path
            existed. Now you do. And now you have someone walking it with you.
          </p>
          <p className="font-bold text-zinc-900">
            Print this. Keep it close. Take it one week at a time. The mothers and babies
            you’re built to serve are already on their way.
          </p>
        </Section>

        {/* FOOTER */}
        <footer className="border-t border-zinc-200 pt-8 mt-12 text-center print:mt-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lightning weight="fill" className="w-4 h-4 text-emerald-600" />
            <span className="font-bold text-zinc-900">FlashGrants</span>
          </div>
          <p className="text-xs text-zinc-500 font-mono">
            Built in Myrtle Beach · For South Carolina founders nobody else is building for
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            Personalized for Ark of Refuge · Prepared June 16, 2026 · flash-grants.com
          </p>
        </footer>
      </main>

      {/* Print-specific tweaks */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export default PlaybookArkOfRefuge;
